import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { HelpCircle, UserPen } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

interface WelcomeSectionProps {
  userName?: string
  sport?: string
  gameCount?: number
}

export function WelcomeSection({
  userName,
  sport = 'baseball',
  gameCount = 3
}: WelcomeSectionProps) {
  const { openUserProfile } = useAuth()

  // Get dynamic greeting based on time of day
  const greeting = useMemo(() => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }, [])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 md:p-3 xl:p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5 md:mb-1">
            <h1 className="font-bebas-neue text-[clamp(18px,4.5vw,30px)] xl:text-[clamp(18px,1.4vw,26px)] font-medium tracking-[2px] uppercase text-foreground">
              {greeting}{userName ? `, ${userName}` : ''}!
            </h1>
            <button
              onClick={openUserProfile}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Edit profile"
              title="Edit profile"
            >
              <UserPen className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>
          <p className="font-oswald text-[clamp(9px,2.2vw,14px)] xl:text-[clamp(10px,0.7vw,13px)] font-medium text-muted-foreground uppercase tracking-[1px]">
            Ready for today's {sport} challenges? {gameCount} new games await.
          </p>
        </div>

        {/* Help Button - vertically stacked on mobile, horizontal on tablet+ */}
        <Link
          to="/help"
          className="flex-shrink-0 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <HelpCircle className="w-4 h-4 text-muted-foreground" />
          <span className="font-oswald text-[clamp(10px,2.5vw,13px)] xl:text-[clamp(10px,0.7vw,12px)] font-medium text-muted-foreground uppercase tracking-[0.5px] text-center sm:text-left">
            Help &<br className="sm:hidden" /> Comments
          </span>
        </Link>
      </div>
    </div>
  )
}